import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  Chip,
  MenuItem,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { productsAPI, apiUtils } from '../services/api';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code_name: '',
    sku: '',
    category: '',
    unit: '',
    price: '',
    cost: '',
    warning_stock: '',
    initial_stock: '',
    img_url: '',
    is_bundle: 0,
    favorite: 0,
    active: 1,
    bundled_products: [],
  });
  const [error, setError] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bundleQuantity, setBundleQuantity] = useState(1);

  // Load products from API
  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  // Update available products when products or editing state changes
  useEffect(() => {
    const filtered = products.filter(product => 
      product.is_bundle === 0 && // Exclude bundles from being bundled
      product.active === 1 && // Only active products
      product.id !== editingProduct?.id // Exclude current product when editing
    );
    setAvailableProducts(filtered);
  }, [products, editingProduct]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.list();
      setProducts(apiUtils.formatResponse(response) || []);
      
    } catch (error) {
      setError(apiUtils.handleError(error));
      // Fallback to mock data if API fails
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await productsAPI.categories();
      const categoriesData = apiUtils.formatResponse(response) || [];
      // Extract unique category names from the response
      const categoryNames = categoriesData.map(item => item.category).filter(Boolean);
      setCategories([...new Set(categoryNames)].sort());
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Fallback categories
      setCategories(['Beverages', 'Snacks', 'Main Course', 'Appetizers', 'Desserts']);
    }
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleStatus(params.row)}
            color={params.row.active === 1 ? 'warning' : 'success'}
          >
            {params.row.active === 1 ? <LockIcon /> : <LockOpenIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Product Name', width: 200 },
    {
      field: 'img_url',
      headerName: 'Image',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        params.value ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={`${process.env.REACT_APP_IMG_HOST}/api/v1/images/${params.value}`}
              alt="Product"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '4px',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        ) : null
      ),
    },
    { field: 'sku', headerName: 'SKU', width: 120 },
    { field: 'code_name', headerName: 'Code', width: 120 },
    { field: 'category', headerName: 'Category', width: 130 },
    { field: 'unit', headerName: 'Unit', width: 80 },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      valueFormatter: (value) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? '₱0.00' : `₱${numValue.toFixed(2)}`;
      },
    },
    {
      field: 'cost',
      headerName: 'Cost',
      width: 100,
      valueFormatter: (value) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? '₱0.00' : `₱${numValue.toFixed(2)}`;
      },
    },
    { field: 'on_hand', headerName: 'On Hand', width: 90 },
    { field: 'warning_stock', headerName: 'Warning Stock', width: 120 },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? 'Active' : 'Inactive'}
          color={params.value === 1 ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'is_bundle',
      headerName: 'Bundle',
      width: 120,
      renderCell: (params) => {
        if (params.value === 1) {
          const bundleCount = params.row.bundle_details?.length || 0;
          return (
            <Chip
              label={`Bundle (${bundleCount})`}
              color="warning"
              size="small"
            />
          );
        }
        return (
          <Chip
            label="Regular"
            color="default"
            size="small"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setEditingProduct(null);
    setSelectedImageFile(null);
    setFormData({
      name: '',
      description: '',
      code_name: '',
      sku: '',
      category: '',
      unit: '',
      price: '',
      cost: '',
      warning_stock: '',
      initial_stock: '',
      img_url: '',
      is_bundle: 0,
      favorite: 0,
      active: 1,
      bundled_products: [],
    });
    setDialogOpen(true);
  };

  const handleEdit = async (product) => {
    console.log('Editing product:', product.id); // Debug log
    
    try {
      // Fetch complete product data including bundle details
      const response = await productsAPI.fetch(product.id);
      const fullProductDataResponse = apiUtils.formatResponse(response);

      if (!fullProductDataResponse || fullProductDataResponse.length === 0) {
        setError('Failed to load product details');
        return;
      }

      console.log('Full product data:', fullProductDataResponse[0]); // Debug log
      const fullProductData = fullProductDataResponse[0];
      setEditingProduct(fullProductData);
      setSelectedImageFile(null);
      setFormData({
        name: fullProductData.name || '',
        description: fullProductData.description || '',
        code_name: fullProductData.code_name || '',
        sku: fullProductData.sku || '',
        category: fullProductData.category || '',
        unit: fullProductData.unit || '',
        price: fullProductData.price || '',
        cost: fullProductData.cost || '',
        warning_stock: fullProductData.warning_stock || '',
        initial_stock: fullProductData.initial_stock || '',
        img_url: fullProductData.img_url || '',
        is_bundle: fullProductData.is_bundle || 0,
        favorite: fullProductData.favorite || 0,
        active: fullProductData.active || 1,
        bundled_products: fullProductData.bundle_details || [],
      });
      setDialogOpen(true);
    } catch (error) {
      console.error('Error fetching product details:', error);
      setError('Failed to load product details for editing');
    }
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.active === 1 ? 0 : 1;
    try {
      await productsAPI.update(product.id, { ...product, active: newStatus });
      setProducts(products.map(p => 
        p.id === product.id 
          ? { ...p, active: newStatus }
          : p
      ));
    } catch (error) {
      console.error('Error toggling product status:', error);
      setError('Failed to update product status');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsAPI.delete(productId);
        setProducts(products.filter(p => p.id !== productId));
      } catch (error) {
        setError(apiUtils.handleError(error));
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price || !formData.sku) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('code_name', formData.code_name);
      formDataToSend.append('sku', formData.sku);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('unit', formData.unit);
      formDataToSend.append('price', parseFloat(formData.price) || 0);
      formDataToSend.append('cost', parseFloat(formData.cost) || 0);
      formDataToSend.append('warning_stock', parseFloat(formData.warning_stock) || 0);
      formDataToSend.append('is_bundle', parseInt(formData.is_bundle) || 0);
      formDataToSend.append('favorite', parseInt(formData.favorite) || 0);
      formDataToSend.append('active', parseInt(formData.active) || 1);

      // Add bundled products if this is a bundle
      if (parseInt(formData.is_bundle) === 1 && formData.bundled_products.length > 0) {
        formDataToSend.append('bundled_products', JSON.stringify(formData.bundled_products));
      }

      // Add initial_stock only for new products (not updates)
      if (!editingProduct && formData.initial_stock) {
        formDataToSend.append('initial_stock', parseFloat(formData.initial_stock) || 0);
      }

      // Add image file if selected
      if (selectedImageFile) {
        formDataToSend.append('image', selectedImageFile);
      } else if (formData.img_url && !editingProduct) {
        // For backward compatibility with existing base64 upload method
        formDataToSend.append('img_url', formData.img_url);
      } else if (editingProduct && formData.img_url) {
        // Preserve existing image URL for updates
        formDataToSend.append('img_url', formData.img_url);
      }

      if (editingProduct) {
        // Update existing product
        const response = await productsAPI.updateWithFile(editingProduct.id, formDataToSend);
        const updatedProduct = apiUtils.formatResponse(response);
        setProducts(products.map(p => 
          p.id === editingProduct.id 
            ? { ...p, ...updatedProduct }
            : p
        ));
      } else {
        // Add new product
        console.log('Creating new product with data:', formDataToSend);

        const response = await productsAPI.createWithFile(formDataToSend);
        const newProduct = apiUtils.formatResponse(response);
        setProducts([...products, newProduct]);
      }

      setDialogOpen(false);
      setError('');
      setSelectedImageFile(null);
    } catch (error) {
      setError(apiUtils.handleError(error));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear bundled products when switching from bundle to non-bundle
    if (name === 'is_bundle' && parseInt(value) === 0) {
      setFormData({
        ...formData,
        [name]: value,
        bundled_products: [],
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Bundle product management functions
  const addBundleProduct = () => {
    if (!selectedProduct || bundleQuantity <= 0) return;
    
    // Check if product is already in the bundle
    const existingIndex = formData.bundled_products.findIndex(
      bundle => bundle.product_id === selectedProduct.id
    );
    
    if (existingIndex >= 0) {
      // Update existing bundle product quantity
      const updatedBundles = [...formData.bundled_products];
      updatedBundles[existingIndex].qty = bundleQuantity;
      setFormData({ ...formData, bundled_products: updatedBundles });
    } else {
      // Add new bundle product
      const newBundleProduct = {
        product_id: selectedProduct.id,
        qty: bundleQuantity,
        product: selectedProduct, // Include product details for display
      };
      setFormData({ 
        ...formData, 
        bundled_products: [...formData.bundled_products, newBundleProduct] 
      });
    }
    
    // Reset selection
    setSelectedProduct(null);
    setBundleQuantity(1);
  };

  const removeBundleProduct = (index) => {
    const updatedBundles = formData.bundled_products.filter((_, i) => i !== index);
    setFormData({ ...formData, bundled_products: updatedBundles });
  };

  const updateBundleQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) return;
    const updatedBundles = [...formData.bundled_products];
    updatedBundles[index].qty = newQuantity;
    setFormData({ ...formData, bundled_products: updatedBundles });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    // Store the file for later upload during save
    setSelectedImageFile(file);
    
    // Generate preview URL for display
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      img_url: previewUrl
    }));

    setError('');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center',
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Typography variant="h4" component="h1">
          Products Management
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          height: 'calc(100vh - 140px)', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={products}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          disableVirtualization={false}
          sx={{
            height: '100%',
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '52px',
              backgroundColor: 'background.paper',
              justifyContent: 'flex-start',
              '& .MuiTablePagination-root': {
                marginLeft: 0,
              },
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'flex-start',
              },
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setSelectedImageFile(null);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {editingProduct ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>
        <DialogContent 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
            },
          }}
        >
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Product Image Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Product Image
            </Typography>
            
            
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              {formData.img_url && formData.img_url.trim() && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 300,
                    height: 200,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <img
                    src={formData.img_url.startsWith('blob:') 
                      ? formData.img_url 
                      : formData.img_url.startsWith('http') 
                        ? formData.img_url
                        : `${process.env.REACT_APP_IMG_HOST}/api/v1/images/${formData.img_url}`
                    }
                    alt="Product preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
              {(!formData.img_url || !formData.img_url.trim()) && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 300,
                    height: 200,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'text.secondary',
                    backgroundColor: 'action.hover',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    No Image
                  </Typography>
                  <Typography variant="body2">
                    Upload a product image
                  </Typography>
                </Box>
              )}
            </Box>

            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <Button
                variant="contained"
                component="span"
                fullWidth
                disabled={uploading}
                sx={{ mb: 2 }}
              >
                {uploading ? 'Uploading...' : 'Choose Image File'}
              </Button>
            </label>
            
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Supported formats: JPG, PNG, GIF • Maximum size: 5MB
            </Typography>
          </Box>

          {/* Basic Information Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            
            <TextField
              name="name"
              label="Product Name *"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="sku"
              label="SKU *"
              value={formData.sku}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="code_name"
              label="Code Name"
              value={formData.code_name}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            
            <Autocomplete
              freeSolo
              options={categories}
              value={formData.category}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  category: newValue || '',
                });
              }}
              onInputChange={(event, newInputValue) => {
                setFormData({
                  ...formData,
                  category: newInputValue || '',
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="category"
                  label="Category"
                  fullWidth
                  placeholder="Select or type a new category"
                />
              )}
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="unit"
              label="Unit"
              value={formData.unit}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Pricing Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Pricing & Stock
            </Typography>
            
            <TextField
              name="price"
              label="Price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                startAdornment: '₱',
              }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              name="cost"
              label="Cost"
              type="number"
              value={formData.cost}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                startAdornment: '₱',
              }}
              sx={{ mb: 2 }}
            />
            
            {!editingProduct && (
              <TextField
                name="initial_stock"
                label="Initial Stock"
                type="number"
                value={formData.initial_stock}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
            
            <TextField
              name="warning_stock"
              label="Warning Stock Level"
              type="number"
              value={formData.warning_stock}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Settings Section */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
              Settings
            </Typography>
            
            <TextField
              name="is_bundle"
              label="Is Bundle Product?"
              select
              value={formData.is_bundle}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </TextField>

            {/* Bundle Products Section */}
            {parseInt(formData.is_bundle) === 1 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Bundle Products
                </Typography>
                
                {/* Add Bundle Product */}
                <Box sx={{ p: 2, border: '1px dashed #ddd', borderRadius: 1, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs sx={{ flexGrow: 1 }}>
                      <Autocomplete
                        options={availableProducts}
                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                        value={selectedProduct}
                        onChange={(event, newValue) => setSelectedProduct(newValue)}
                        fullWidth
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Select Product" 
                            size="small"
                            fullWidth
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <TextField
                        label="Quantity"
                        type="number"
                        value={bundleQuantity}
                        onChange={(e) => setBundleQuantity(parseFloat(e.target.value) || 1)}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ min: 1, step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Button
                        variant="contained"
                        onClick={addBundleProduct}
                        disabled={!selectedProduct || bundleQuantity <= 0}
                        fullWidth
                        size="small"
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {/* Bundle Products List */}
                {formData.bundled_products.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.bundled_products.map((bundleProduct, index) => (
                          <TableRow key={bundleProduct.product_id || index}>
                            <TableCell>
                              {bundleProduct.product?.name || 'Unknown Product'}
                            </TableCell>
                            <TableCell>
                              {bundleProduct.product?.sku || '-'}
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={bundleProduct.qty}
                                onChange={(e) => updateBundleQuantity(index, parseFloat(e.target.value) || 1)}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 1, step: 0.1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeBundleProduct(index)}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {formData.bundled_products.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No products added to bundle yet.
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              name="favorite"
              label="Mark as Favorite?"
              select
              value={formData.favorite}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </TextField>
            
            <TextField
              name="active"
              label="Status"
              select
              value={formData.active}
              onChange={handleInputChange}
              fullWidth
              required
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setSelectedImageFile(null);
            setSelectedProduct(null);
            setBundleQuantity(1);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            {editingProduct ? 'Update' : 'Add'} Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Add Product */}
      <Fab
        color="primary"
        aria-label="add product"
        onClick={handleAdd}
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 24,
          zIndex: 1000,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Products;
